<script setup lang="ts">
import { i18n } from '@/i18n';
import { storeToRefs } from 'pinia';
import { useTrashBinStore } from './useTrashBinStore';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent';
import { onMounted, ref } from 'vue';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import ControlViewTab from '../components/ControlViewTab.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';

const trashBinStore = useTrashBinStore();
const { state } = storeToRefs(trashBinStore);
const t = i18n.t;
let renderingKey = 0;

const {
  $reset,
  sortTableContentItems,
  reloadTable,
  onChangePaging,
  fetchTableContentProperties,
  fetchItems,
  calcDeleteBusiness,
  deleteAggregateBusiness,
  deleteBusiness,
  updateIsTrash,
  itemCheck,
  onClickTrashBinAllSelect,
} = trashBinStore;

const isRefreshTable = ref(false);

onMounted(async () => {
  await init();
});

/**
 * 初期化処理
 * @param viewValue - 初期化時のタブ
 */
const init = async (viewValue?: 'aggregateBusiness' | 'business') => {
  $reset();
  state.value.nowView = viewValue ?? 'aggregateBusiness';
  state.value.tableContentProperties = fetchTableContentProperties();
  fetchItems(state.value.nowView);

  renderingKey += 1;
};

/**
 * 画面の表示を変更
 * @param viewValue - 変更する画面
 */
const controlViewTabOnClick = (viewValue: 'aggregateBusiness' | 'business') => {
  init(viewValue);
  reloadTable();
};

/**
 * 削除ボタン押下時の処理
 */
const deleteButtonOnClick = () => {
  switch (state.value.nowView) {
    case 'aggregateBusiness':
      state.value.isDeleteAggregateBusinessModalOpened = true;
      return;
    case 'business':
      state.value.isDeleteBusinessModalOpened = true;
  }
  return;
};

/**
 * 復元ボタン押下時の処理
 */
const recoverButtonOnClick = () => {
  state.value.isUpdateModalOpened = true;
  return;
};

/**
 * ページを更新する処理
 */
const reloadPage = () => {
  location.reload();
  return;
};

/**
 * 集計削除モーダル確定押下時の処理
 */
const deleteAggregateBusinessModalNext = async () => {
  await deleteAggregateBusiness();
  if (state.value.isErrorModalOpened) return;
  init(state.value.nowView);
  onChangePaging({
    pageSize: state.value.pagingOptions.pageSize,
    offset: 0,
    nextOffset: undefined,
    totalResultsAvailable: undefined,
    returnCount: 0,
  });
  reloadTable();
  isRefreshTable.value = true;
  isRefreshTable.value = false;
};

/**
 * 取引削除モーダル確定押下時の処理
 */
const deleteBusinessModalNext = async () => {
  await deleteBusiness();
  if (state.value.isErrorModalOpened) return;
  init(state.value.nowView);
  onChangePaging({
    pageSize: state.value.pagingOptions.pageSize,
    offset: 0,
    nextOffset: undefined,
    totalResultsAvailable: undefined,
    returnCount: 0,
  });
  reloadTable();
  isRefreshTable.value = true;
  isRefreshTable.value = false;
};

/**
 * 復元モーダル確定押下時の処理
 */
const updateModalNext = async () => {
  await updateIsTrash();
  if (state.value.isErrorModalOpened) return;
  init(state.value.nowView);
  onChangePaging({
    pageSize: state.value.pagingOptions.pageSize,
    offset: 0,
    nextOffset: undefined,
    totalResultsAvailable: undefined,
    returnCount: 0,
  });
  reloadTable();
  isRefreshTable.value = true;
  isRefreshTable.value = false;
};
</script>
<template>
  <div class="trash-bin__page-wrap">
    <div class="trash-bin__page-title">{{ t('ゴミ箱') }}</div>
    <div class="trash-bin__table-container">
      <div class="trash-bin__table-header">
        <TextButton
          button-style="btn-primary"
          size="btn-md"
          :on-click="deleteButtonOnClick"
          class="trash-bin__button"
          :disabled="!!!state.checkedIds.length"
        >
          {{ t('完全削除する') }}
        </TextButton>
        <TextButton
          button-style="btn-secondary"
          size="btn-md"
          :on-click="recoverButtonOnClick"
          class="trash-bin__button"
          :disabled="!!!state.checkedIds.length"
        >
          {{ t('管理表に戻す') }}
        </TextButton>
        <div class="trash-bin__info-text">{{ t('ゴミ箱の取引は、45日後に自動的に削除されます。') }}</div>
        <!-- Filterについては現状未実装 -->
        <ControlViewTab class="trash-bin__control-view-tab" :now-view="state.nowView" @click="controlViewTabOnClick" />
        <Pagination
          :paging-options="state.pagingOptions"
          :is-loading="state.loadingState.status.type === 'loading'"
          class="pagination"
          @on-change-page="onChangePaging($event)"
        />
      </div>
      <div class="trash-bin__table-contains">
        <NewBaseTableContent
          :key="renderingKey"
          :prop-table-content-properties="state.tableContentProperties"
          :prop-display-objects="state.tableContentItems"
          :prop-loading-status="state.loadingState"
          :prop-table-type="'trash'"
          :prop-is-reset="isRefreshTable"
          :prop-all-of-result="state.pagingOptions.totalResultsAvailable"
          :prop-checked-ids="state.checkedIds"
          :prop-is-all-selected="state.isAllSelected"
          class="trash-bin-items-table__contains trash-bin-items-table__contains--table"
          @checked="itemCheck($event)"
          @on-click-sort="(sortKey: TableContentProperty, isAsc: boolean) => sortTableContentItems(sortKey, isAsc)"
          @reload="reloadTable"
          @on-click-trash-bin-all-select="onClickTrashBinAllSelect"
        />
      </div>
    </div>
    <InfoModal
      :modal-state="state.isDeleteAggregateBusinessModalOpened"
      icon="error"
      color-style="error"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('削除')"
      @on-back="
        () => {
          state.isDeleteAggregateBusinessModalOpened = false;
          return;
        }
      "
      @on-next="deleteAggregateBusinessModalNext()"
    >
      <template #title>{{ t('取引を完全削除します') }}</template>
      <template #description>
        {{ t('削除モーダル関連取引削除', { businessesNumber: calcDeleteBusiness() }) }}<br />
        {{ t('削除された取引は復元できません。') }}<br />
        {{ t('よろしいですか？') }}
      </template>
    </InfoModal>
    <InfoModal
      :modal-state="state.isDeleteBusinessModalOpened"
      icon="error"
      color-style="error"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('削除')"
      @on-back="
        () => {
          state.isDeleteBusinessModalOpened = false;
          return;
        }
      "
      @on-next="deleteBusinessModalNext()"
    >
      <template #title>{{ t('取引を完全削除します') }}</template>
      <template #description>
        {{ t('削除された取引は復元できません。') }}<br />
        {{ t('よろしいですか？') }}
      </template>
    </InfoModal>
    <InfoModal
      :modal-state="state.isUpdateModalOpened"
      icon="info"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('戻す')"
      @on-back="
        () => {
          state.isUpdateModalOpened = false;
          return;
        }
      "
      @on-next="updateModalNext()"
    >
      <template #title>{{ t('取引を管理表に戻します') }}</template>
      <template #description>
        {{ t('選択された取引を管理表に復活させます。') }}<br />
        {{ t('よろしいですか？') }}
      </template>
    </InfoModal>
    <InfoModal :modal-state="state.isErrorModalOpened" icon="error" color-style="error" next-button-label="確認" @on-next="reloadPage()">
      <template #title>{{ t('エラーが発生しました。') }}</template>
      <template #description>
        <p>
          {{ t('再度やり直してください。') }}
        </p>
      </template>
    </InfoModal>
  </div>
</template>
<style lang="scss" scoped>
.trash-bin {
  &__page-wrap {
    padding-top: 16px;
    padding-left: 16px;
    padding-right: 16px;
  }
  min-width: calc(100% - 32px);
  overflow-x: scroll;
  overflow-y: scroll;
  margin-right: 16px;

  &__page-title {
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0em;
    line-height: 27px;
    padding-bottom: 16px;
  }

  &__table-header {
    display: grid;
    grid-template-columns: max-content max-content 1fr;
    align-items: center;
    gap: 16px;
  }

  &__control-view-tab {
    margin-bottom: 16px;
  }
}

.pagination {
  grid-column: 3 / 4;
  justify-self: end;
}
</style>
